// src/lib/seo.ts — Metadata generation helpers

import type { Metadata } from 'next';
import type { Question, Category } from '@/types';

const BASE_URL = 'https://dartdosage.fluxpert.com';
const SITE_NAME = 'dartdosage';

export function generateQuestionMetadata(question: Question): Metadata {
  return {
    title: question.title,
    description: question.shortAnswer,
    keywords: [...question.tags, 'flutter interview questions', 'dart', 'dartdosage'],
    openGraph: {
      title: question.title,
      description: question.shortAnswer,
      type: 'article',
      url: `${BASE_URL}/questions/${question.slug}`,
      siteName: SITE_NAME,
      images: [
        {
          url: `${BASE_URL}/og-image.svg`,
          width: 1200,
          height: 630,
          alt: `${question.title} | dartdosage`,
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      title: question.title,
      description: question.shortAnswer,
      images: [`${BASE_URL}/og-image.svg`],
    },
    alternates: {
      canonical: `${BASE_URL}/questions/${question.slug}`,
    },
  };
}

export function generateCategoryMetadata(category: Category): Metadata {
  return {
    title: `${category.name} — Flutter Interview Questions`,
    description: `${category.questionCount} Flutter interview questions on ${category.name}. ${category.description}. Practice with dartdosage.`,
    keywords: [category.name.toLowerCase(), 'flutter interview', 'dart', 'dartdosage'],
    openGraph: {
      title: `${category.name} | dartdosage`,
      description: category.description,
      type: 'website',
      url: `${BASE_URL}/categories/${category.slug}`,
      siteName: SITE_NAME,
    },
    alternates: {
      canonical: `${BASE_URL}/categories/${category.slug}`,
    },
  };
}

export function generateFAQSchema(question: Question): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: question.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: question.shortAnswer,
        },
      },
    ],
  };
}

export function generateBreadcrumbSchema(
  items: Array<{ name: string; url?: string }>
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}
